/**
 * hashtags.ts
 *
 * Utility functions for hashtag extraction and manipulation.
 * Uses the same regex pattern as the backend for consistency.
 */

/**
 * Extract hashtags from text using the same regex as backend
 * Returns lowercase, deduplicated array
 *
 * @param text - Text to extract hashtags from
 * @returns Array of lowercase hashtag strings (without # prefix)
 *
 * @example
 * extractHashtags("Hello #World #Test #world")
 * // Returns: ["world", "test"]
 */
export function extractHashtags(text: string): string[] {
  if (!text) return [];

  const normalized = text.normalize('NFC');
  const withoutUrls = normalized.replace(/https?:\/\/\S+/gu, ' '); // keep URL fragments like .../side#anker out
  const regex = /#[\p{L}\p{N}\p{M}_]+/gu; // match anywhere, like the old ASCII regex
  const matches = withoutUrls.match(regex) || [];

  // Remove # prefix, lowercase, deduplicate
  return Array.from(new Set(
    matches
      .map(tag => tag.slice(1).toLowerCase())
      .filter(tag => /\p{L}/u.test(tag)) // must contain a letter: excludes #123, #_
  ));
}

/**
 * Remove a specific hashtag from text
 * Removes all occurrences (case-insensitive)
 *
 * @param text - Text to remove hashtag from
 * @param hashtag - Hashtag to remove (without # prefix)
 * @returns Text with hashtag removed
 *
 * @example
 * removeHashtag("Hello #World #test", "world")
 * // Returns: "Hello #test"
 */
export function removeHashtag(text: string, hashtag: string): string {
  if (!text || !hashtag) return text;

  // NFC-normalize both sides so decomposed input matches
  const normText = text.normalize('NFC');
  const tag = hashtag.replace(/^#/, '').normalize('NFC');
  if (!tag) return text;

  // Unicode-aware boundary: same class as extractHashtags (no lookbehind, Safari-safe)
  const regex = new RegExp(`#${escapeRegExp(tag)}(?![\\p{L}\\p{N}\\p{M}_])`, 'giu');

  // Remove hashtag and clean up extra spaces
  return normText
    .replace(regex, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Add hashtags to text (with deduplication)
 * Appends new hashtags on a new line if not already present
 *
 * @param text - Existing text
 * @param hashtags - Hashtags to add (without # prefix)
 * @returns Text with new hashtags appended
 *
 * @example
 * addHashtags("Hello world", ["test", "social"])
 * // Returns: "Hello world\n#test #social"
 *
 * addHashtags("Hello #test", ["test", "social"])
 * // Returns: "Hello #test\n#social"
 */
export function addHashtags(text: string, hashtags: string[]): string {
  if (!hashtags || hashtags.length === 0) return text;

  const existing = extractHashtags(text);

  // Filter out hashtags that already exist (case-insensitive)
  // Also normalize hashtags by removing any leading # (in case they're passed with it)
  const newTags = hashtags
    .map(tag => tag.replace(/^#/, ''))
    .filter(tag => !existing.includes(tag.toLowerCase()));

  if (newTags.length === 0) return text;

  const formatted = newTags.map(tag => `#${tag}`).join(' ');

  // Append on new line if text exists, otherwise just return tags
  return text.trim() ? `${text}\n${formatted}` : formatted;
}

/**
 * Escape special regex characters
 * Used internally to safely create regex patterns from user input
 */
function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Validate hashtag format
 * Checks if a hashtag is valid according to our rules:
 * - Unicode letters supported (e.g. æ, ö, ł); must contain at least one letter
 * - Can contain letters, numbers, underscores
 * - No spaces or special characters
 *
 * @param hashtag - Hashtag to validate (with or without # prefix)
 * @returns true if valid, false otherwise
 *
 * @example
 * isValidHashtag("test123") // true
 * isValidHashtag("#test_123") // true
 * isValidHashtag("123test") // true (contains a letter)
 * isValidHashtag("123") // false (no letter)
 * isValidHashtag("test-tag") // false (no hyphens)
 */
export function isValidHashtag(hashtag: string): boolean {
  if (!hashtag) return false;

  // Remove # prefix if present
  const tag = (hashtag.startsWith('#') ? hashtag.slice(1) : hashtag).normalize('NFC');
  return /^[\p{L}\p{N}\p{M}_]+$/u.test(tag) && /\p{L}/u.test(tag);
}

/**
 * Count hashtags in text
 *
 * @param text - Text to count hashtags in
 * @returns Number of unique hashtags
 *
 * @example
 * countHashtags("Hello #World #Test #world")
 * // Returns: 2 (world and test, deduplicated)
 */
export function countHashtags(text: string): number {
  return extractHashtags(text).length;
}
